/**
 * @vhire/urls — Canonical URL module for the VHire monorepo.
 *
 * Single source of truth for:
 *   - Default hosts (root domain, API gateway, app, assets)
 *   - Tenant URL construction (subdomain or custom domain)
 *   - Reserved subdomain enforcement
 *   - CORS decisions (vhire-domain, custom-domain, no-origin-but-authed)
 *   - The edge HTTP contract: which headers a browser page may send the
 *     gateway and read back (./http-contract)
 *   - Agreement link builders: every agreement page address an email or a
 *     service emits (./agreement-links)
 *
 * Guiding principle: every URL in the system is derived from tenant context,
 * not from environment variables. Env vars are scaffolding for the *defaults*.
 */

// Read process.env when present without requiring Node globals in consumers.
// Cloudflare Worker packages compile without `@types/node`, while Next and
// Node callers still receive their environment overrides at runtime.
const runtimeProcess = (globalThis as {
  process?: { env?: Record<string, string | undefined> };
}).process;
const env = runtimeProcess?.env ?? {};

export const ROOT_DOMAIN = env.ROOT_DOMAIN ?? 'vhire.com';
export const API_BASE = env.API_BASE_URL ?? `https://api.${ROOT_DOMAIN}`;
export const APP_BASE = env.APP_BASE_URL ?? `https://${ROOT_DOMAIN}`;
export const ASSETS_BASE = env.ASSETS_BASE_URL ?? `https://assets.${ROOT_DOMAIN}`;
export const WS_BASE = API_BASE.replace(/^https/, 'wss');

/**
 * Tenant URL context — what the URL builders need to produce a tenant-correct URL.
 *
 * `tenantId` is the canonical key (slugs and custom domains are derived from it).
 * Always pass it when available so cache invalidation and OAuth state stay
 * stable across slug renames.
 */
export interface TenantUrlContext {
  tenantId?: string;
  slug: string;
  customDomain?: string | null;
}

/**
 * Build the canonical origin for a tenant. Prefers a verified custom domain
 * over the {slug}.{ROOT_DOMAIN} default.
 */
export function tenantUrl(t: TenantUrlContext): string {
  if (t.customDomain) return `https://${t.customDomain}`;
  return `https://${t.slug}.${ROOT_DOMAIN}`;
}

/**
 * Build a full URL on a tenant's origin (or APP_BASE if no tenant).
 * Use for magic links, share URLs, OAuth redirect targets — anything that
 * lands a user back on their tenant.
 */
export function buildAuthLink(opts: {
  tenant?: TenantUrlContext;
  path: string;
  query?: Record<string, string>;
}): string {
  const base = opts.tenant ? tenantUrl(opts.tenant) : APP_BASE;
  const u = new URL(opts.path, base);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    u.searchParams.set(k, v);
  }
  return u.toString();
}

/**
 * Reserved subdomain matcher. Defense in depth: regex catches families
 * (test-*, cdn[0-9]*) and the explicit set catches the known operational names.
 *
 * Tenant signup MUST reject any slug for which this returns true.
 */
const RESERVED_REGEX = /^(api|app|assets|admin|auth|cdn[0-9]*|dashboard|docs|edge|event|files|help|ingest|jobs|mail|mx|resumes|smtp|staging|static|status|support|users|www|ws|analytics|documents|test|test-[a-z0-9-]+|careers)$/i;

export const RESERVED_SUBDOMAINS: ReadonlySet<string> = new Set([
  'api', 'app', 'assets', 'admin', 'auth', 'careers', 'cdn', 'dashboard',
  'docs', 'documents', 'edge', 'event', 'files', 'help', 'ingest', 'jobs',
  'mail', 'mx', 'resumes', 'smtp', 'staging', 'static', 'status',
  'support', 'users', 'www', 'ws', 'analytics',
]);

export function isReservedSubdomain(slug: string): boolean {
  const s = slug.toLowerCase();
  return RESERVED_REGEX.test(s) || RESERVED_SUBDOMAINS.has(s);
}

/**
 * Origin allowlist for CORS, link-trust checks, etc.
 *
 * Allows any `*.{ROOT_DOMAIN}` origin (including the apex), plus any host
 * present in `customDomains` (typically the verified custom-domain list for
 * the requesting tenant).
 */
export function vhireOriginRegex(): RegExp {
  return new RegExp(`^https:\\/\\/(?:[a-z0-9-]+\\.)?${ROOT_DOMAIN.replace(/\./g, '\\.')}$`);
}

export function isAllowedOrigin(
  origin: string | null | undefined,
  customDomains: string[] = []
): boolean {
  if (!origin) return false;
  if (vhireOriginRegex().test(origin)) return true;
  try {
    const host = new URL(origin).host;
    return customDomains.includes(host);
  } catch {
    return false;
  }
}

/**
 * CORS decision for a single request. Handles three real-world cases:
 *   1. Browser request from a vhire-family origin → allow.
 *   2. Browser request from a known custom-domain origin → allow.
 *   3. Non-browser request (curl, cron, server-to-server) with no Origin →
 *      allow if the request carries a valid auth credential.
 *
 * The auth check is intentionally lenient (just "is something there?") because
 * actual token verification happens later in the request lifecycle. CORS only
 * decides whether the response gets an Access-Control-Allow-Origin header.
 */
export type CorsReason =
  | 'vhire-domain'
  | 'custom-domain'
  | 'no-origin-but-authed'
  | 'no-origin-no-auth'
  | 'no-match';

export interface CorsDecision {
  allow: boolean;
  reason: CorsReason;
}

function hasAuthCredential(req: Request): boolean {
  const auth = req.headers.get('Authorization');
  if (auth && auth.trim().length > 0) return true;
  const apiKey = req.headers.get('X-API-Key') || req.headers.get('x-api-key');
  if (apiKey && apiKey.trim().length > 0) return true;
  return false;
}

export function corsDecision(req: Request, customDomains: string[] = []): CorsDecision {
  const origin = req.headers.get('Origin');

  if (!origin) {
    return hasAuthCredential(req)
      ? { allow: true, reason: 'no-origin-but-authed' }
      : { allow: false, reason: 'no-origin-no-auth' };
  }

  if (vhireOriginRegex().test(origin)) {
    return { allow: true, reason: 'vhire-domain' };
  }

  try {
    const host = new URL(origin).host;
    if (customDomains.includes(host)) {
      return { allow: true, reason: 'custom-domain' };
    }
  } catch {
    // fall through to no-match
  }

  return { allow: false, reason: 'no-match' };
}

export * from './http-contract';
export * from './agreement-links';
